import { z } from "zod";

const translationPath = "chatBot.page.templates.whatsapp_template.errors.";

const customButtonSchema = z.object({
  id: z.string().nullable(),
  order: z.number(),
  text: z
    .string()
    .min(1, translationPath + "custom_button_text")
    .max(25, translationPath + "custom_button_text_max"),
});

const actionTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  value: z.string().optional(),
});

const actionsTypeSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "url_type" },
);

const urlTypeSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
    description: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "url_type" },
);

const countrySchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string(),
    ddiCode: z.string(),
    phoneMask: z.string(),
  },
  { invalid_type_error: translationPath + "country" },
);

const ctaButtonSchema = z
  .object({
    id: z.string().optional(),
    order: z.number(),
    actionType: actionTypeSchema,
    text: z
      .string()
      .min(1, translationPath + "text")
      .max(25, translationPath + "text_max"),
    urlType: urlTypeSchema.optional(),
    country: countrySchema.optional(),
    payload: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.actionType.id !== "4") {
      if (!val.payload || val.payload.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "payload",
          path: ["payload"],
        });
      }
    }
  });

const ticketServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
});

const headerTypeSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "header_type" },
);

const ticketAutomationInternalTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
});
const ticketAutomationSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
    actionTypes: z.array(actionTypeSchema).nullish(),
  },
  { invalid_type_error: translationPath + "ticket_automation" },
);

const whatsMetaCategorySchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "whatsMetaCategory" },
);

const genericSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
});

const mediaFileSchema = z.object({
  mediaType: genericSchema.optional(),
  id: z.string().min(1, translationPath + "file"),
  mediaTypeId: z.string().optional(),
  isPublic: z.boolean(),
  fileName: z.string(),
  cdnDomain: z.string(),
  filePath: z.string(),
  mimeType: z.string(),
  fileSize: z.number(),
  file: z.instanceof(File).optional(),
});

const bodyExampleSchema = z.object({
  example: z.string().min(1, translationPath + "example"),
});

const characterLimitRequestSchema = z.object({
  ticketAutomationMacroId: z.string(),
  name: z.string(),
  characterLimit: z.string(),
});

export const whatsappTemplateSchema = z.object({
  id: z.string().optional(),
  enable: z.boolean(),
  fileFixed: z.boolean().nullish(),
  whatsMetaCategory: whatsMetaCategorySchema,
  name: z.string().min(1, translationPath + "name"),
  ticketService: ticketServiceSchema.nullish(),
  headerType: headerTypeSchema.nullish(),
  mediaFile: mediaFileSchema.nullish(),
  ticketAutomation: ticketAutomationSchema,
  actionType: actionsTypeSchema.nullish(),
  ticketAutomationInternalTemplate:
    ticketAutomationInternalTemplateSchema.nullish(),
  title: z.string().optional(),
  body: z.string().min(1, translationPath + "body"),
  footer: z.string().optional(),
  customButtons: z.array(customButtonSchema).optional(),
  ctaButtons: z.array(ctaButtonSchema).optional(),
  bodyExamples: z.array(bodyExampleSchema).optional(),
  characterLimitRequests: z.array(characterLimitRequestSchema).optional(),
  invalidVariables: z.boolean(),
});

export type TWhatsappTemplateForm = z.infer<typeof whatsappTemplateSchema>;
